// src/pages/super-admin/SuperAdminAcademicCalendar.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../api/client';
import { FireLoader } from '../../components/Loader';
import { showConfirm, showError, showSuccess } from '../../utils/alert';

const makeEmptyForm = () => ({
  year: '',
  name: '',
  start_date: '',
  end_date: '',
  remarks: 'Official MBSSE Sierra Leone academic calendar',
  terms: [1, 2, 3].map((term_number) => ({ term_number, start_date: '', end_date: '' })),
});

const prettyDate = (value) => value
  ? new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  : 'Not set';

export default function SuperAdminAcademicCalendar() {
  const [years, setYears] = useState([]);
  const [form, setForm] = useState(makeEmptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/super-admin/academic-calendar');
      setYears(response.data?.data || []);
    } catch (error) {
      await showError({ title: 'Unable to Load Calendar', text: error.response?.data?.message || 'The global MBSSE calendar could not be loaded.' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setTerm = (index, field, value) => setForm((current) => ({
    ...current,
    terms: current.terms.map((term, position) => position === index ? { ...term, [field]: value } : term),
  }));

  const cancelEdit = () => {
    setEditingId(null);
    setForm(makeEmptyForm());
  };

  const beginEdit = (academicYear) => {
    const terms = [...(academicYear.terms || [])].sort((a, b) => a.term_number - b.term_number);
    setEditingId(academicYear.id);
    setForm({
      year: academicYear.year || '',
      name: academicYear.name || '',
      start_date: academicYear.start_date || '',
      end_date: academicYear.end_date || '',
      remarks: academicYear.remarks || '',
      terms: [1, 2, 3].map((term_number) => {
        const term = terms.find((item) => Number(item.term_number) === term_number);
        return { term_number, start_date: term?.start_date || '', end_date: term?.end_date || '' };
      }),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (editingId) await apiClient.put(`/super-admin/academic-calendar/${editingId}`, form);
      else await apiClient.post('/super-admin/academic-calendar', form);

      await showSuccess({
        title: editingId ? 'Calendar Updated' : 'Calendar Published',
        text: editingId
          ? 'The updated official dates now apply globally to every school and plan payment.'
          : 'The official MBSSE academic calendar is now used by every school and all plan payments.',
      });
      cancelEdit();
      await load();
    } catch (error) {
      await showError({
        title: editingId ? 'Unable to Update Calendar' : 'Unable to Publish Calendar',
        text: error.response?.data?.message || 'Please check all official dates and try again.',
      });
    } finally { setSaving(false); }
  };

  const removeYear = async (academicYear) => {
    const confirmed = await showConfirm({
      title: 'Delete Academic Year?',
      text: `${academicYear.name} will be removed from use. Existing finance and payment history will be preserved.`,
      confirmText: 'Delete year',
      icon: 'warning',
    });
    if (!confirmed) return;

    setDeletingId(academicYear.id);
    try {
      await apiClient.delete(`/super-admin/academic-calendar/${academicYear.id}`);
      if (editingId === academicYear.id) cancelEdit();
      await showSuccess({ title: 'Academic Year Deleted', text: 'The year and its terms are no longer available for new plan payments.' });
      await load();
    } catch (error) {
      await showError({ title: 'Unable to Delete Academic Year', text: error.response?.data?.message || 'The academic year could not be deleted.' });
    } finally { setDeletingId(null); }
  };

  if (loading) return <FireLoader fullScreen size="lg" text="Loading..." />;

  return <div className="space-y-6">
    <Helmet><title>MBSSE Calendar | PawaSchool Super Admin</title></Helmet>
    <div><h1 className="text-2xl font-bold text-slate-900">Global MBSSE Calendar</h1><p className="mt-1 text-sm text-slate-500">Create, edit or safely delete Sierra Leone’s official school dates for every PawaSchool school.</p></div>
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
      <div className="mb-5 flex items-center justify-between gap-3"><div className="flex items-center gap-3"><span className="rounded-xl bg-primary-50 p-3 text-primary-600"><CalendarDaysIcon className="h-6 w-6" /></span><div><h2 className="font-bold text-slate-900">{editingId ? 'Edit academic year' : 'Publish academic year'}</h2><p className="text-xs text-slate-400">Use only dates officially published by MBSSE.</p></div></div>{editingId && <button type="button" onClick={cancelEdit} className="flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100"><XMarkIcon className="h-4 w-4" /> Cancel edit</button>}</div>
      <div className="grid gap-4 md:grid-cols-2"><label className="text-sm font-semibold text-slate-600">Academic year<input required value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value, name: `${e.target.value} Academic Session` })} placeholder="2026/2027" pattern="[0-9]{4}/[0-9]{4}" className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-primary-400" /></label><label className="text-sm font-semibold text-slate-600">Display name<input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="2026/2027 Academic Session" className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-primary-400" /></label><label className="text-sm font-semibold text-slate-600">Academic year starts<input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5" /></label><label className="text-sm font-semibold text-slate-600">Academic year ends<input required type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5" /></label></div>
      <div className="mt-6 grid gap-4 lg:grid-cols-3">{form.terms.map((term, index) => <section key={term.term_number} className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><h3 className="font-bold text-slate-800">Term {term.term_number}</h3><label className="mt-3 block text-xs font-semibold text-slate-500">Official opening date<input required type="date" value={term.start_date} onChange={(e) => setTerm(index, 'start_date', e.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5" /></label><label className="mt-3 block text-xs font-semibold text-slate-500">Official closing date<input required type="date" value={term.end_date} onChange={(e) => setTerm(index, 'end_date', e.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5" /></label></section>)}</div>
      <button disabled={saving} className="mt-6 rounded-xl bg-primary-600 px-5 py-3 text-sm font-bold text-white hover:bg-primary-700 disabled:opacity-50">{saving ? (editingId ? 'Saving changes...' : 'Publishing...') : (editingId ? 'Save Calendar Changes' : 'Publish Global Calendar')}</button>
    </form>
    <div className="space-y-4">
      {years.length ? years.map((year) => (
        <article key={year.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-bold text-slate-900">{year.name}</h2>
                <p className="text-xs text-slate-400">{prettyDate(year.start_date)} – {prettyDate(year.end_date)}</p>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${year.is_current ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                {year.is_current ? 'Current year' : year.status}
              </span>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {(year.terms || []).map((term) => (
                <div key={term.id} className="rounded-xl bg-slate-50 p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-slate-700">{term.short_name}</p>
                    {term.is_current && <CheckCircleIcon className="h-5 w-5 text-emerald-500" />}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{prettyDate(term.start_date)} – {prettyDate(term.end_date)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4">
            <p className="text-xs font-semibold text-slate-500">Manage this academic year</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => beginEdit(year)}
                className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-primary-700"
              >
                <PencilSquareIcon className="h-5 w-5" />
                Edit Year
              </button>
              <button
                type="button"
                disabled={deletingId === year.id}
                onClick={() => removeYear(year)}
                className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-bold text-red-600 transition hover:bg-red-50 disabled:opacity-50"
              >
                <TrashIcon className="h-5 w-5" />
                {deletingId === year.id ? 'Deleting...' : 'Delete Year'}
              </button>
            </div>
          </div>
        </article>
      )) : (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-400">
          No academic years have been published yet.
        </div>
      )}
    </div>
  </div>;
}